import { Box } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuth } from 'src/hooks/useAuth';

export type BulkUploadParams = {
  url: string;
};

export const PrizetBulkUpload = (props: BulkUploadParams) => {
  const { url } = props;
  const { enhancedFetch } = useAuth();
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles);
    setUploadError(null); // Reset error state
    setUploadSuccess(false); // Reset success state
  }, []);

  useEffect(() => {
    if (!files.length) return;

    const uploadFile = async () => {
      setIsLoading(true);
      setUploadSuccess(false); // Reset success state
      setUploadError(null); // Reset error state

      const formData = new FormData();
      const file = files[0];
      formData.append('file', file, file.name);

      try {
        const response = await enhancedFetch(url, {
          method: 'POST',
          body: formData
        });
        setUploadSuccess(true);
        setFiles([]);
      } catch (error) {
        console.error('Upload error:', error);
        setUploadError(error.message || 'Upload failed');
        setUploadSuccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    uploadFile();
  }, [files, url]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'] // Only accept CSV files
    }
  });

  return (
    <Box display={'flex'} flexDirection={'column'} sx={{ width: '100%' }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px',
          borderWidth: 2,
          borderRadius: 1,
          borderColor: '#eeeeee',
          borderStyle: 'dashed',
          backgroundColor: '#fafafa',
          color: '#bdbdbd',
          outline: 'none',
          transition: 'border .24s ease-in-out'
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the CSV file here ...</p>
        ) : (
          <p>Drag 'n' drop a CSV file here, or click to select a file</p>
        )}
      </Box>

      {/* Upload Feedback */}
      {isLoading && <p>Uploading...</p>}

      {/* File List */}
      <aside>
        <h4>Files</h4>
        <ul>
          {files.map((file) => (
            <li key={file.name}>
              {file.name} - {file.size} bytes
            </li>
          ))}
        </ul>
      </aside>
    </Box>
  );
};
